import { useEffect, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

const EMOJIS = ['⌂','◎','◎','◎','◎','◎','◎','◎','◎','◎','◎','↗','$','◎','◎','◎','◎','◎','◎','◎'];
const COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#14b8a6','#3b82f6','#6366f1','#9f7aea','#ec4899','#c6a96b','#06b6d4','#10b981'];

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const isMobile = useIsMobile();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#6366f1', icon: '◎', budget_monthly: '', keywords: '' });

  const load = async () => {
    const cats = await api.getCategories();
    setCategories(cats);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', color: '#6366f1', icon: '◎', budget_monthly: '', keywords: '' });
    setShowModal(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, color: cat.color, icon: cat.icon, budget_monthly: cat.budget_monthly || '', keywords: (cat.keywords || []).join(', ') });
    setShowModal(true);
  };

  const save = async () => {
    const body = {
      name: form.name,
      color: form.color,
      icon: form.icon,
      budget_monthly: form.budget_monthly ? parseFloat(form.budget_monthly) : null,
      keywords: form.keywords.split(',').map(k => k.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.updateCategory(editing.id, body);
        toast.success('Category updated');
      } else {
        await api.createCategory(body);
        toast.success('Category created');
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete category? Transactions will become uncategorized.')) return;
    await api.deleteCategory(id);
    toast.success('Deleted');
    load();
  };

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-title" style={{ marginBottom: 2 }}>Categories</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>Manage how transactions are grouped and auto-matched</div>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ New Category</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Monthly Budget</th>
              <th>Keywords (for auto-matching)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => (
              <tr key={cat.id}>
                <td>
                  <div className="flex items-center gap-3">
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: cat.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{cat.icon}</div>
                    <span style={{ fontWeight: 500 }}>{cat.name}</span>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.color }} />
                  </div>
                </td>
                <td>
                  {cat.budget_monthly ? (
                    <span style={{ color: 'var(--accent)', fontWeight: 500 }}>${cat.budget_monthly}/mo</span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 400 }}>
                    {(cat.keywords || []).slice(0, 6).map(kw => (
                      <span key={kw} style={{ background: 'var(--bg-elevated)', padding: '2px 7px', borderRadius: 4, fontSize: 11, color: 'var(--text-secondary)' }}>{kw}</span>
                    ))}
                    {(cat.keywords || []).length > 6 && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>+{cat.keywords.length - 6} more</span>}
                    {(cat.keywords || []).length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No keywords (AI only)</span>}
                  </div>
                </td>
                <td>
                  <div className="flex gap-2">
                    <button className="btn btn-sm btn-ghost" onClick={() => openEdit(cat)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => del(cat.id)}>×</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? 'Edit Category' : 'New Category'}</div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Name</label>
                <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Category name" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Monthly Budget</label>
                <input className="input" type="number" value={form.budget_monthly} onChange={e => setForm(f => ({ ...f, budget_monthly: e.target.value }))} placeholder="0.00 (optional)" />
              </div>
            </div>

            <div className="form-group mt-4">
              <label className="form-label">Icon</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {EMOJIS.map(e => (
                  <button key={e} onClick={() => setForm(f => ({ ...f, icon: e }))}
                    style={{ width: 36, height: 36, borderRadius: 8, border: `2px solid ${form.icon === e ? 'var(--accent)' : 'var(--border)'}`, background: 'var(--bg-elevated)', cursor: 'pointer', fontSize: 18 }}>
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Color</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {COLORS.map(c => (
                  <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                    style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: `3px solid ${form.color === c ? 'white' : 'transparent'}`, cursor: 'pointer' }} />
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Keywords (comma-separated)</label>
              <input className="input" value={form.keywords} onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))} placeholder="starbucks, coffee, cafe, doordash" />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Transactions matching these words are auto-assigned to this category</div>
            </div>

            <div className="flex gap-3 justify-between mt-4">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={!form.name}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
